import NoticeMessageLayout from '#/atoms/notice-message-layout';
import {MessageContent} from '#/repository/types';
import {ClassName} from '#/utils/utils';
import {CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon} from '@heroicons/react/24/outline';
import {ArrowUpRightIcon, XMarkIcon} from '@heroicons/react/24/solid';
import {ComponentProps, FunctionComponent, ReactNode} from 'react';

export const NoticeMessage: FunctionComponent<
  Pick<ComponentProps<typeof NoticeMessageLayout>, 'theme'> &
    Omit<MessageContent, 'canSubmitForm' | 'content' | 'theme'> &
    ClassName & {
      children: ReactNode;
    }
> = ({theme, action, actionButtonTitle, className = '', children}) => {
  const {
    IconComponent,
    iconColorClassName,
    backgroundColor,
    ActionButtonIconComponent,
    actionButtonIconColorClassName,
  } = noticeMessageThemeData[theme];

  return (
    <NoticeMessageLayout
      className={`space-y-3 shrink-0 ${className}`}
      pxClassName='px-3'
      pyClassName='py-3'
      theme={theme}
    >
      <div className='flex items-center gap-3'>
        <IconComponent className={`shrink-0 w-7 h-7 mx-0.5 stroke-[1.5] ${iconColorClassName}`} />

        <div className='grow text-sm font-semibold text-left'>{children}</div>

        {action && (
          <button
            className={`h-6 text-xs font-bold self-start px-1.5 py-1 ${backgroundColor} text-grayblue/80 rounded-lg flex justify-end items-center gap-1 whitespace-nowrap hover:opacity-70`}
            onClick={action}
          >
            <ActionButtonIconComponent className={`w-3 h-3 ${actionButtonIconColorClassName}`} />
            {actionButtonTitle}
          </button>
        )}
      </div>
    </NoticeMessageLayout>
  );
};

const noticeMessageThemeData: Record<
  ComponentProps<typeof NoticeMessage>['theme'],
  {
    IconComponent: FunctionComponent<ComponentProps<typeof CheckCircleIcon>>;
    iconColorClassName: string;
    backgroundColor: string;
    ActionButtonIconComponent: FunctionComponent<ComponentProps<typeof ArrowUpRightIcon>>;
    actionButtonIconColorClassName: string;
  }
> = {
  success: {
    IconComponent: CheckCircleIcon,
    iconColorClassName: 'stroke-greenNotice',
    backgroundColor: 'bg-greenNotice',
    ActionButtonIconComponent: ArrowUpRightIcon,
    actionButtonIconColorClassName: 'fill-grayblue/80 stroke-grayblue/80',
  },
  warning: {
    IconComponent: ExclamationTriangleIcon,
    iconColorClassName: 'stroke-yellowNotice',
    backgroundColor: 'bg-yellowNotice',
    ActionButtonIconComponent: ArrowUpRightIcon,
    actionButtonIconColorClassName: 'fill-grayblue/80 stroke-grayblue/80',
  },
  error: {
    IconComponent: ExclamationCircleIcon,
    iconColorClassName: 'stroke-redNotice',
    backgroundColor: 'bg-redNotice',
    ActionButtonIconComponent: ArrowUpRightIcon,
    actionButtonIconColorClassName: 'fill-grayblue/80 stroke-grayblue/80',
  },
  notification: {
    IconComponent: ExclamationTriangleIcon,
    iconColorClassName: 'stroke-grayblue',
    backgroundColor: 'bg-grayblue/20',
    ActionButtonIconComponent: XMarkIcon,
    actionButtonIconColorClassName: 'fill-grayblue stroke-grayblue',
  },
};
